import { useCallback, useRef, useState } from "react";
import { X, Zap, ZapOff, ArrowRight, CameraOff, Ghost, ImageUp, Images } from "lucide-react";
import { cn } from "@/lib/utils";
import type { CapturedPhoto } from "@/components/common/record-capture/captured-photo";
import { stampPhoto } from "@/components/common/record-capture/stamp-photo";
import { useCameraStream, ZOOM_EPSILON } from "./use-camera-stream";
import type { CameraStatus } from "./use-camera-stream";
import { useLandscapeUnlock, useOrientation } from "./use-orientation";

/**
 * Letterboxed in-app camera overlay. Live `getUserMedia` viewfinder, a shutter
 * that freezes the current frame into a `CapturedPhoto`, feature-detected
 * flash/torch, and permission/no-camera fallbacks. A "Single photo · Multi
 * photo" segmented toggle picks the capture mode: in `single` a shot leaves the
 * camera straight to markup; in `multi` shots append to a batch and the stacked
 * thumbnail + "Mark up" affordances appear. In single mode the bottom-left deck
 * slot is a gallery button that opens the device library; in multi mode that
 * slot holds the stacked thumbnail. The preview is letterboxed into a 3:4 box
 * (the full 4:3 sensor, WYSIWYG with capture) above a solid black control bar.
 * When the device offers more than the default lens, a zoom-chip row overlays
 * the bottom of the preview; the active chip shows the live factor, inactive
 * chips their bare label. The batch is owned by the orchestrator. Top bar is
 * Close + Flash only — no plan chip. When the device is landscape the overlay
 * reflows — the preview widens to 4:3 and the controls move to a vertical
 * rail on the thumb-side edge (Close overlays the preview, Flash sits atop
 * the rail); the single `<video>` element is preserved across rotation, so
 * the stream is never re-acquired. A host may pass a `ghostUrl` — an earlier
 * photo of the scene laid over the preview at 40% so the new shot can be lined
 * up with it — with a Ghost toggle beside Flash that is on at every open. The
 * ghost is DOM over the video, so `captureFrame` never includes it. See
 * [[burst-camera.spec]].
 */

export type CameraMode = "single" | "multi";

export interface CameraCaptureProps {
  isOpen: boolean;
  /** Fired once per shutter press with a freshly minted CapturedPhoto. */
  onCapture: (photo: CapturedPhoto) => void;
  /** Close the camera without capturing. */
  onClose: () => void;
  /** Batch size — drives the stacked thumbnail + "Mark up" affordance (multi mode only). */
  batchCount: number;
  /** Newest photo's preview URL — the face of the stacked thumbnail. */
  latestPreviewUrl?: string | null;
  /** Jump to the markup step (tapping the stack or "Mark up"). */
  onReview: () => void;
  /** Fall back to the device library picker (from the denied / no-camera UI). */
  onUseLibrary?: () => void;
  /** Capture mode. `single` → straight to markup; `multi` → append to batch. */
  mode: CameraMode;
  /** Fired when the user taps the segmented "Single photo" / "Multi photo" toggle. */
  onModeChange: (mode: CameraMode) => void;
  /**
   * Open the device gallery/library to pick an existing photo. Wired to the
   * bottom-left gallery button shown only in single mode (the multi-mode slot
   * holds the stacked thumbnail instead).
   */
  onPickFromLibrary: () => void;
  /**
   * When true, the "Single photo · Multi photo" segmented toggle is NOT
   * rendered and the camera stays single-shot. Additive and backward-compatible
   * — default (`undefined`/`false`) renders the toggle as today. See
   * [[burst-camera.spec]].
   */
  hideModeToggle?: boolean;
  /**
   * An earlier photo of the scene, drawn faded over the live preview with a
   * Ghost toggle to show / hide it. Omit (or null) for no ghost and no toggle.
   * See [[burst-camera.spec]] Decision #9.
   */
  ghostUrl?: string | null;
  /** Label of the multi-mode pill beside the shutter. Defaults to "Mark up". */
  reviewLabel?: string;
}

export function CameraCapture({
  isOpen,
  onCapture,
  onClose,
  batchCount,
  latestPreviewUrl,
  onReview,
  onUseLibrary,
  mode,
  onModeChange,
  onPickFromLibrary,
  hideModeToggle = false,
  ghostUrl = null,
  reviewLabel = "Mark up",
}: CameraCaptureProps) {
  const {
    videoRef,
    status,
    torchSupported,
    torchOn,
    toggleTorch,
    captureFrame,
    retry,
    lensStops,
    activeZoom,
    zoomFloor,
    zoomCeiling,
    previewScale,
    setZoom,
    selectStop,
  } = useCameraStream(isOpen);

  const [isFlashing, setIsFlashing] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  // The ghost is on at every open, never remembered: reset while rendering the
  // closed → open transition rather than in an effect, which would paint one
  // frame with the stale choice first.
  const [ghostOn, setGhostOn] = useState(true);
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setGhostOn(true);
  }
  // A ghost that fails to load counts as no ghost — no dead toggle. Keyed on the
  // URL so a host swapping in a better source gets a fresh attempt.
  const [failedGhostUrl, setFailedGhostUrl] = useState<string | null>(null);

  // Orientation is a pure viewport read — it drives the CSS reflow only and
  // never re-acquires or re-constrains the stream (the 2026-06-18 iOS guardrail).
  const orientation = useOrientation();
  const isLandscape = orientation !== "portrait";
  const isSecondary = orientation === "landscape-secondary";
  // Android: unlock orientation for the overlay while open, restore portrait on
  // exit. No-op on iOS / non-installed tabs. See [[burst-camera.spec]] #8.
  useLandscapeUnlock(isOpen);

  // Pinch-to-zoom gesture state. Two active pointers → track their distance
  // ratio off a baseline and drive setZoom (clamped in the hook). Bound to the
  // viewfinder only when the track can zoom (`zoomCeiling > zoomFloor`).
  const pointersRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const pinchBaseRef = useRef<{ dist: number; zoom: number } | null>(null);
  const dist = (a: { x: number; y: number }, b: { x: number; y: number }) =>
    Math.hypot(a.x - b.x, a.y - b.y);

  // Anchor the pinch baseline to the two pointers currently down, off the current
  // factor — including a sub-1× factor on a fused camera, where pinch-out to 0.5×
  // is real. Re-anchoring keeps a 3→2 finger transition from jumping.
  const capturePinchBase = () => {
    const [a, b] = [...pointersRef.current.values()];
    pinchBaseRef.current = { dist: dist(a, b), zoom: activeZoom };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* unsupported — the gesture still works while fingers stay in-bounds */
    }
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) capturePinchBase();
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!pointersRef.current.has(e.pointerId)) return;
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const base = pinchBaseRef.current;
    if (base && pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      setZoom(base.zoom * (dist(a, b) / base.dist));
    }
  };
  const endPointer = (e: React.PointerEvent) => {
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* not captured / unsupported — safe to ignore */
    }
    pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchBaseRef.current = null;
    else if (pointersRef.current.size === 2) capturePinchBase();
  };

  const handleShutter = useCallback(async () => {
    if (status !== "ready" || isCapturing) return;
    setIsCapturing(true);
    setIsFlashing(true);
    window.setTimeout(() => setIsFlashing(false), 320);
    try {
      const blob = await captureFrame();
      const id = crypto.randomUUID();
      const takenAtHint = new Date().toISOString();
      const originalFile = new File([blob], `capture-${id}.jpg`, {
        type: "image/jpeg",
        lastModified: Date.now(),
      });
      // Stamp the frame straight away rather than waiting for the markup
      // editor: Done only ever commits the ONE photo the user opened, so an
      // unstamped `annotatedBlob` here is what a burst uploads for every photo
      // the user never taps into ([[record-capture.spec]] US-008).
      const annotatedBlob = await stampPhoto(originalFile);
      const photo: CapturedPhoto = {
        id,
        originalFile,
        renderFile: originalFile,
        annotatedBlob,
        drawings: [],
        previewUrl: URL.createObjectURL(annotatedBlob),
        takenAtHint,
      };
      onCapture(photo);
    } catch (err) {
      console.error("Camera frame capture failed:", err);
    } finally {
      setIsCapturing(false);
    }
  }, [status, isCapturing, captureFrame, onCapture]);

  if (!isOpen) return null;

  const showStack = mode === "multi" && batchCount >= 1;
  const isReady = status === "ready";
  const hasGhost = isReady && !!ghostUrl && ghostUrl !== failedGhostUrl;
  const ghostButton = hasGhost ? (
    <GhostButton ghostOn={ghostOn} onToggle={() => setGhostOn((on) => !on)} />
  ) : null;
  const canPinch = isReady && zoomCeiling > zoomFloor;
  const eligibleStops = lensStops.filter((s) => s <= activeZoom + ZOOM_EPSILON);
  const activeStop = eligibleStops.length
    ? Math.max(...eligibleStops)
    : Math.min(...lensStops);

  const pinchHandlers = {
    onPointerDown,
    onPointerMove,
    onPointerUp: endPointer,
    onPointerCancel: endPointer,
  };

  return (
    <div
      className={cn(
        "fixed inset-0 z-[60] flex bg-black text-white",
        isLandscape ? (isSecondary ? "flex-row-reverse" : "flex-row") : "flex-col",
      )}
      role="dialog"
      aria-modal="true"
      aria-label="Camera"
    >
      {/* Portrait top bar (Close + Flash). In landscape, Close overlays the
          preview and Flash moves to the rail, so this bar is portrait-only. */}
      {!isLandscape && (
        <div
          key="topbar"
          className="z-[8] flex shrink-0 items-center gap-2 bg-black px-3.5 pb-3"
          style={{ paddingTop: "max(env(safe-area-inset-top, 0px), 12px)" }}
        >
          <IconButton aria-label="Close" onClick={onClose}>
            <X className="h-5 w-5" />
          </IconButton>
          <div className="flex-1" />
          {ghostButton}
          {isReady && torchSupported === true && (
            <FlashButton torchOn={torchOn} onToggle={() => void toggleTorch()} />
          )}
        </div>
      )}

      {/* Preview region — ALWAYS rendered with a stable key so the single
          <video> node is PRESERVED across the portrait<->landscape reflow (no
          remount = the stream binding survives = no black screen). Only the
          Viewfinder box aspect and the Close overlay change with orientation. */}
      <div
        key="preview"
        className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden"
      >
        {isLandscape && (
          <IconButton
            aria-label="Close"
            onClick={onClose}
            className="absolute z-[8]"
            style={{
              top: "max(env(safe-area-inset-top, 0px), 12px)",
              left: "max(env(safe-area-inset-left, 0px), 12px)",
            }}
          >
            <X className="h-5 w-5" />
          </IconButton>
        )}
        <Viewfinder
          landscape={isLandscape}
          videoRef={videoRef}
          isReady={isReady}
          previewScale={previewScale}
          canPinch={canPinch}
          pinchHandlers={pinchHandlers}
          lensStops={lensStops}
          activeStop={activeStop}
          activeZoom={activeZoom}
          onSelectStop={selectStop}
          ghostUrl={hasGhost && ghostOn ? ghostUrl : null}
          onGhostError={setFailedGhostUrl}
        />
        <PreviewStatusOverlays status={status} onRetry={retry} onUseLibrary={onUseLibrary} />
      </div>

      {/* Controls — portrait bottom bar OR landscape rail. Genuinely different
          layouts; this subtree may reconcile/remount on reflow (fine — no stream
          lives here). */}
      {!isLandscape ? (
        <div
          key="controls"
          className="flex shrink-0 flex-col items-center gap-4 bg-black pt-4"
          style={{ paddingBottom: "max(env(safe-area-inset-bottom, 0px), 18px)" }}
        >
          {isReady && !hideModeToggle && (
            <ModeToggle orientation="horizontal" mode={mode} onModeChange={onModeChange} />
          )}
          {isReady && (
            <div className="grid w-full grid-cols-[1fr_auto_1fr] items-center px-6">
              <div className={mode === "single" ? "justify-self-center" : "justify-self-start"}>
                <LeftDeck
                  mode={mode}
                  showStack={showStack}
                  batchCount={batchCount}
                  latestPreviewUrl={latestPreviewUrl}
                  onPickFromLibrary={onPickFromLibrary}
                  onReview={onReview}
                />
              </div>
              <Shutter disabled={isCapturing} onPress={() => void handleShutter()} />
              <div className="justify-self-end">
                {showStack && <MarkupPill label={reviewLabel} onReview={onReview} />}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div
          key="controls"
          className="flex shrink-0 flex-col items-center justify-around bg-black py-4"
          style={
            isSecondary
              ? { paddingLeft: "env(safe-area-inset-left, 0px)" }
              : { paddingRight: "env(safe-area-inset-right, 0px)" }
          }
        >
          {/* Ghost sits above Flash, the two sharing one rail slot so a ghost
              doesn't respace the rail around the shutter. */}
          {ghostButton && (
            <div className="flex flex-col items-center gap-3">
              {ghostButton}
              {isReady && torchSupported === true && (
                <FlashButton torchOn={torchOn} onToggle={() => void toggleTorch()} />
              )}
            </div>
          )}
          {!ghostButton && isReady && torchSupported === true && (
            <FlashButton torchOn={torchOn} onToggle={() => void toggleTorch()} />
          )}
          {isReady && !hideModeToggle && (
            <ModeToggle orientation="vertical" mode={mode} onModeChange={onModeChange} />
          )}
          {isReady && <Shutter disabled={isCapturing} onPress={() => void handleShutter()} />}
          {isReady && (
            <LeftDeck
              mode={mode}
              showStack={showStack}
              batchCount={batchCount}
              latestPreviewUrl={latestPreviewUrl}
              onPickFromLibrary={onPickFromLibrary}
              onReview={onReview}
            />
          )}
          {isReady && showStack && <MarkupPill label={reviewLabel} onReview={onReview} />}
        </div>
      )}

      {/* flash fire overlay (shared) */}
      <div
        key="flash"
        className={cn(
          "pointer-events-none absolute inset-0 z-40 bg-white opacity-0",
          isFlashing && "flash-fire",
        )}
      />
    </div>
  );
}

/* ==========================================================================
 * Viewfinder — the preview box: <video> + previewScale transform + zoom chips
 * + pinch handlers. `landscape` flips the box aspect 3:4 -> 4:3. Reused by both
 * arrangements; nothing here touches the stream.
 * ========================================================================== */

interface ViewfinderProps {
  landscape: boolean;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isReady: boolean;
  previewScale: number;
  canPinch: boolean;
  pinchHandlers: {
    onPointerDown: (e: React.PointerEvent) => void;
    onPointerMove: (e: React.PointerEvent) => void;
    onPointerUp: (e: React.PointerEvent) => void;
    onPointerCancel: (e: React.PointerEvent) => void;
  };
  lensStops: number[];
  activeStop: number;
  activeZoom: number;
  onSelectStop: (factor: number) => void;
  /** Ghost photo to draw over the feed, or null for none (absent or toggled off). */
  ghostUrl: string | null;
  /** Reports the URL of a ghost that failed to load. */
  onGhostError: (url: string) => void;
}

function Viewfinder({
  landscape,
  videoRef,
  isReady,
  previewScale,
  canPinch,
  pinchHandlers,
  lensStops,
  activeStop,
  activeZoom,
  onSelectStop,
  ghostUrl,
  onGhostError,
}: ViewfinderProps) {
  return (
    <div
      className={cn(
        "relative overflow-hidden",
        landscape ? "aspect-[4/3] max-h-full max-w-full" : "aspect-[3/4] max-h-full w-full",
      )}
      {...(canPinch
        ? { ...pinchHandlers, style: { touchAction: "none" as const } }
        : {})}
    >
      <video
        ref={videoRef}
        className={cn("h-full w-full object-cover", !isReady && "opacity-0")}
        style={
          previewScale > 1
            ? { transform: `scale(${previewScale})`, transformOrigin: "center" }
            : undefined
        }
        playsInline
        autoPlay
        muted
      />

      {/* ---- ghost: an earlier photo of the scene to line the shot up with ----
          Never scaled with the preview — it marks the target framing, and the
          user zooms the live view to meet it. Remote URLs load with anonymous
          CORS so this <img> can't poison the cache entry the canvas consumers
          of the same S3 URL rely on; `blob:` URLs must not carry it. */}
      {ghostUrl && (
        <img
          src={ghostUrl}
          alt=""
          aria-hidden="true"
          data-testid="camera-ghost"
          crossOrigin={ghostUrl.startsWith("blob:") ? undefined : "anonymous"}
          onError={() => onGhostError(ghostUrl)}
          className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-40"
        />
      )}

      {/* ---- zoom chips: 0.5× / 1× / 2× (/ 3× on capable Android) ---- */}
      {isReady && lensStops.length > 1 && (
        <div
          role="radiogroup"
          aria-label="Lens"
          className="absolute inset-x-0 bottom-3 z-[6] flex justify-center"
        >
          <div className="flex gap-1 rounded-full bg-black/35 p-1 backdrop-blur-[6px]">
            {lensStops.map((stop) => {
              const isActive = stop === activeStop;
              return (
                <StopButton
                  key={stop}
                  ariaLabel={formatZoom(stop)}
                  label={
                    isActive
                      ? formatZoom(Math.round(activeZoom * 10) / 10)
                      : formatStopBare(stop)
                  }
                  active={isActive}
                  onClick={() => onSelectStop(stop)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* Loading spinner + permission/no-camera/error fallback, overlaying the preview
 * region. Shared by both arrangements. */
function PreviewStatusOverlays({
  status,
  onRetry,
  onUseLibrary,
}: {
  status: CameraStatus;
  onRetry: () => void;
  onUseLibrary?: () => void;
}) {
  return (
    <>
      {status === "loading" && (
        <div className="absolute inset-0 grid place-items-center">
          <div
            className="h-9 w-9 animate-spin rounded-full border-2 border-white/30 border-t-white"
            aria-label="Starting camera"
            role="status"
          />
        </div>
      )}
      {(status === "denied" || status === "no-camera" || status === "error") && (
        <CameraFallback status={status} onRetry={onRetry} onUseLibrary={onUseLibrary} />
      )}
    </>
  );
}

/* Feature-detected flash/torch button. Rendered ONLY when the track supports
 * torch — never a dead button. Used in the portrait top bar and the landscape rail. */
function FlashButton({
  torchOn,
  onToggle,
}: {
  torchOn: boolean;
  onToggle: () => void;
}) {
  return (
    <IconButton
      aria-label={torchOn ? "Turn off flash" : "Turn on flash"}
      aria-pressed={torchOn}
      active={torchOn}
      onClick={onToggle}
    >
      {torchOn ? <Zap className="h-5 w-5" /> : <ZapOff className="h-5 w-5" />}
    </IconButton>
  );
}

/* Ghost show / hide. Rendered only when there is a ghost — never a dead button. */
function GhostButton({
  ghostOn,
  onToggle,
}: {
  ghostOn: boolean;
  onToggle: () => void;
}) {
  return (
    <IconButton
      aria-label={ghostOn ? "Hide ghost" : "Show ghost"}
      aria-pressed={ghostOn}
      active={ghostOn}
      onClick={onToggle}
    >
      <Ghost className="h-5 w-5" />
    </IconButton>
  );
}

/* 74px shutter ring. */
function Shutter({
  disabled,
  onPress,
}: {
  disabled: boolean;
  onPress: () => void;
}) {
  return (
    <button
      type="button"
      aria-label="Take photo"
      onClick={onPress}
      disabled={disabled}
      className="grid h-[74px] w-[74px] cursor-pointer place-items-center rounded-full border-4 border-white p-1 transition-transform duration-75 active:scale-[0.94]"
    >
      <span className="h-full w-full rounded-full bg-white transition-transform duration-75 active:scale-90" />
    </button>
  );
}

/* Bottom-deck left slot: gallery/upload button (single) or the WhatsApp-style
 * stacked thumbnail (multi). Returns a fragment so the caller's grid column
 * stays the button's parentElement (the centering assertion depends on it). */
function LeftDeck({
  mode,
  showStack,
  batchCount,
  latestPreviewUrl,
  onPickFromLibrary,
  onReview,
}: {
  mode: CameraMode;
  showStack: boolean;
  batchCount: number;
  latestPreviewUrl?: string | null;
  onPickFromLibrary: () => void;
  onReview: () => void;
}) {
  return (
    <>
      {mode === "single" && (
        <button
          type="button"
          aria-label="Upload from gallery"
          onClick={onPickFromLibrary}
          className="grid h-14 w-14 cursor-pointer place-items-center rounded-2xl bg-white/10 text-white backdrop-blur transition-transform active:scale-95"
        >
          <Images className="h-6 w-6" />
        </button>
      )}
      {showStack && (
        <button
          type="button"
          aria-label="Review photos"
          onClick={onReview}
          className="relative h-14 w-14 cursor-pointer transition-transform active:scale-95"
        >
          <span className="absolute inset-0 -translate-x-[7px] -translate-y-[6px] -rotate-[7deg] overflow-hidden rounded-xl border-2 border-white bg-neutral-700 shadow-[0_6px_14px_rgba(0,0,0,0.45)] brightness-[0.8]" />
          <span className="absolute inset-0 -translate-x-[3px] -translate-y-[3px] -rotate-[3deg] overflow-hidden rounded-xl border-2 border-white bg-neutral-700 shadow-[0_6px_14px_rgba(0,0,0,0.45)] brightness-90" />
          <span className="absolute inset-0 overflow-hidden rounded-xl border-2 border-white bg-neutral-700 shadow-[0_6px_14px_rgba(0,0,0,0.45)]">
            {latestPreviewUrl && (
              <img src={latestPreviewUrl} alt="" className="h-full w-full object-cover" />
            )}
          </span>
          <span className="absolute -right-2 -top-2 z-[5] inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full border-2 border-black bg-primary px-1.5 text-xs font-bold leading-none text-primary-foreground shadow-[0_2px_6px_rgba(0,0,0,0.4)]">
            {batchCount}
          </span>
        </button>
      )}
    </>
  );
}

/* "Mark up" pill (multi mode only); a host may relabel it (e.g. "Done"). */
function MarkupPill({ label, onReview }: { label: string; onReview: () => void }) {
  return (
    <button
      type="button"
      onClick={onReview}
      className="inline-flex h-12 cursor-pointer items-center gap-1.5 rounded-full bg-primary pl-5 pr-4 text-sm font-semibold text-primary-foreground"
    >
      {label}
      <ArrowRight className="h-[18px] w-[18px]" />
    </button>
  );
}

/* "Single photo · Multi photo" toggle. `horizontal` (grid-cols-2) in portrait;
 * `vertical` (grid-cols-1, stacked) on the landscape rail. */
function ModeToggle({
  orientation,
  mode,
  onModeChange,
}: {
  orientation: "horizontal" | "vertical";
  mode: CameraMode;
  onModeChange: (mode: CameraMode) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Capture mode"
      className={cn(
        "grid gap-1 rounded-full bg-white/10 p-1 backdrop-blur-[6px]",
        orientation === "horizontal" ? "grid-cols-2" : "grid-cols-1",
      )}
    >
      <ModeButton
        label="Single photo"
        active={mode === "single"}
        onClick={() => onModeChange("single")}
      />
      <ModeButton
        label="Multi photo"
        active={mode === "multi"}
        onClick={() => onModeChange("multi")}
      />
    </div>
  );
}

/**
 * Format a numeric zoom factor as a label per [[burst-camera.spec]] "Lens
 * Stops & Zoom": integers stay whole (`1×`, `2×`), fractions show one decimal
 * (`0.5×`, `1.7×`).
 */
function formatZoom(f: number): string {
  return `${Number.isInteger(f) ? f : f.toFixed(1)}×`;
}

/**
 * Bare zoom label for an INACTIVE chip, native-camera style: a sub-1× stop
 * drops the leading zero (`0.5` → `.5`), whole stops render plain (`1`, `2`,
 * `3`). The active chip uses `formatZoom` (the live factor with `×`) instead.
 */
function formatStopBare(n: number): string {
  return n < 1 ? n.toString().replace(/^0/, "") : String(n);
}

/**
 * One zoom chip (0.5× / 1× / 2× / 3×). Same visual language as the mode toggle,
 * ≥56px tap targets. Visible `label` changes (bare `.5`/`1`/`2` inactive, live
 * factor `1.7×` active) but a STABLE `ariaLabel` pins the accessible name.
 */
function StopButton({
  label,
  ariaLabel,
  active,
  onClick,
}: {
  label: string;
  ariaLabel: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-label={ariaLabel}
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "grid h-14 w-14 cursor-pointer place-items-center rounded-full text-[13px] font-semibold tabular-nums tracking-[0.01em] transition-colors",
        active ? "bg-white text-black" : "text-white/70 hover:text-white",
      )}
    >
      {label}
    </button>
  );
}

/**
 * One segment of the "Single photo · Multi photo" toggle. `role="radio"` with
 * `aria-checked`. Active = white pill / black text; inactive = translucent white.
 */
function ModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "h-9 w-full cursor-pointer rounded-full px-4 text-center text-[13px] font-semibold tracking-[0.01em] transition-colors",
        active ? "bg-white text-black" : "text-white/70 hover:text-white",
      )}
    >
      {label}
    </button>
  );
}

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  active?: boolean;
}

function IconButton({ active, className, children, ...rest }: IconButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        "inline-flex h-10 w-10 items-center justify-center rounded-full backdrop-blur-[6px] transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "bg-black/30 text-white hover:bg-black/50",
        className,
      )}
      {...rest}
    >
      {children}
    </button>
  );
}

function CameraFallback({
  status,
  onRetry,
  onUseLibrary,
}: {
  status: "denied" | "no-camera" | "error";
  onRetry: () => void;
  onUseLibrary?: () => void;
}) {
  const copy = {
    denied: {
      title: "Camera access denied",
      body: "Allow camera access, then try again — or pick a photo from your library.",
    },
    "no-camera": {
      title: "No camera found",
      body: "We couldn't find a camera on this device. You can pick a photo from your library instead.",
    },
    error: {
      title: "Camera unavailable",
      body: "Something went wrong starting the camera. Try again, or pick a photo from your library.",
    },
  }[status];

  return (
    <div className="absolute inset-0 z-[7] grid place-items-center px-8">
      <div className="flex max-w-xs flex-col items-center text-center">
        <CameraOff className="mb-4 h-10 w-10 text-white/70" />
        <h2 className="text-base font-semibold">{copy.title}</h2>
        <p className="mt-2 text-sm text-white/70">{copy.body}</p>
        <div className="mt-6 flex flex-col items-stretch gap-2.5 self-stretch">
          {status !== "no-camera" && (
            <button
              type="button"
              onClick={onRetry}
              className="h-11 rounded-full bg-white text-sm font-semibold text-black"
            >
              Try again
            </button>
          )}
          {onUseLibrary && (
            <button
              type="button"
              onClick={onUseLibrary}
              className="inline-flex h-11 items-center justify-center gap-2 rounded-full border border-white/30 text-sm font-medium text-white"
            >
              <ImageUp className="h-4 w-4" />
              Use library instead
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
